import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/services/api';
import type { Booking } from '@/types';
import { CACHE_DURATION } from '../utils/cache';

// ─── State ──────────────────────────────────────────────────

interface BookingState {
  bookings: Booking[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: BookingState = {
  bookings: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

type CreateBookingPayload = {
  projectId?: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  summary: string;
  description?: string;
  date: string;
  startTimeLocal: string;
  endTimeLocal: string;
  timezone?: string;
  bookingType?: 'event' | 'meeting';
  eventType?: string;
  ignoreConflicts?: boolean;
};

const getThunkErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null) {
    const candidate = error as { response?: { data?: { message?: unknown; error?: unknown } } };
    const responseMessage = candidate.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage;
    }
    const responseError = candidate.response?.data?.error;
    if (typeof responseError === 'string' && responseError.trim()) {
      return responseError;
    }
  }

  return fallback;
};

// ─── Async Thunks ───────────────────────────────────────────

export const fetchBookings = createAsyncThunk(
  'bookings/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const force = options?.force ?? false;
    const state = (getState() as { bookings: BookingState }).bookings;
    const now = Date.now();

    // Return cached data if recent and not forced
    if (
      !force &&
      state.bookings.length > 0 &&
      state.lastFetched &&
      now - state.lastFetched < CACHE_DURATION
    ) {
      return state.bookings;
    }

    try {
      const response = await api.get<Booking[]>('/bookings');
      if (response && Array.isArray(response)) {
        return [...response].sort(
          (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
        );
      }
      return [];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to load bookings');
    }
  }
);

export const approveBooking = createAsyncThunk(
  'bookings/approve',
  async (
    { bookingId, skipEmail = false }: { bookingId: string; skipEmail?: boolean },
    { rejectWithValue }
  ) => {
    try {
      await api.post(`/bookings/${bookingId}/approve`, { skipEmail });
      return bookingId;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to approve booking');
    }
  }
);

export const createBooking = createAsyncThunk(
  'bookings/create',
  async (payload: CreateBookingPayload, { rejectWithValue }) => {
    try {
      return await api.post<Booking>('/bookings', payload);
    } catch (err) {
      return rejectWithValue(getThunkErrorMessage(err, 'Failed to create booking'));
    }
  }
);

export const declineBooking = createAsyncThunk(
  'bookings/decline',
  async ({ bookingId, reason }: { bookingId: string; reason?: string }, { rejectWithValue }) => {
    try {
      await api.post(`/bookings/${bookingId}/decline`, { reason });
      return bookingId;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to decline booking');
    }
  }
);

export const requestReschedule = createAsyncThunk(
  'bookings/reschedule',
  async ({ bookingId, reason }: { bookingId: string; reason?: string }, { rejectWithValue }) => {
    const trimmedReason = reason?.trim();
    const payloadReason =
      trimmedReason && trimmedReason.length > 0
        ? trimmedReason
        : 'Please choose a different time that works for you.';
    try {
      await api.post(`/bookings/${bookingId}/reschedule`, { reason: payloadReason });
      return bookingId;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to request reschedule');
    }
  }
);

export const updateBooking = createAsyncThunk(
  'bookings/update',
  async (
    {
      bookingId,
      updates,
    }: {
      bookingId: string;
      updates: Partial<
        Pick<
          Booking,
          'startTime' | 'endTime' | 'status' | 'summary' | 'bookingType' | 'eventType' | 'projectId'
        >
      >;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch<Partial<Booking> & { id: string }>(
        `/bookings/${bookingId}`,
        updates
      );
      return {
        bookingId,
        updates,
        data: response,
      };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to update booking');
    }
  }
);

export const deleteBooking = createAsyncThunk(
  'bookings/delete',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/bookings/${bookingId}`);
      return bookingId;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to delete booking');
    }
  }
);

// ─── Slice ──────────────────────────────────────────────────

const bookingSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    clearBookings: state => {
      state.bookings = [];
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: builder => {
    // fetchBookings
    builder
      .addCase(createBooking.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.isSaving = false;
        state.bookings = [action.payload, ...state.bookings].sort(
          (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
        );
        state.lastFetched = Date.now();
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to create booking';
      });

    builder
      .addCase(fetchBookings.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.bookings = action.payload;
        state.isLoading = false;
        state.lastFetched = Date.now();
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to load bookings';
      });

    // approveBooking — optimistic: mark confirmed, refetch on error
    builder
      .addCase(approveBooking.pending, (state, action) => {
        state.isSaving = true;
        const booking = state.bookings.find(b => b.id === action.meta.arg.bookingId);
        if (booking) booking.status = 'confirmed';
      })
      .addCase(approveBooking.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(approveBooking.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to approve booking';
        // Invalidate cache so next render refetches
        state.lastFetched = null;
      });

    // declineBooking — optimistic: mark cancelled, refetch on error
    builder
      .addCase(declineBooking.pending, (state, action) => {
        state.isSaving = true;
        const booking = state.bookings.find(b => b.id === action.meta.arg.bookingId);
        if (booking) booking.status = 'cancelled';
      })
      .addCase(declineBooking.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(declineBooking.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to decline booking';
        state.lastFetched = null;
      });

    // requestReschedule — optimistic: mark reschedule_requested
    builder
      .addCase(requestReschedule.pending, (state, action) => {
        state.isSaving = true;
        const booking = state.bookings.find(b => b.id === action.meta.arg.bookingId);
        if (booking) booking.status = 'reschedule_requested';
      })
      .addCase(requestReschedule.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(requestReschedule.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to request reschedule';
        state.lastFetched = null;
      });

    // updateBooking — optimistic update for direct edits
    builder
      .addCase(updateBooking.pending, (state, action) => {
        state.isSaving = true;
        const booking = state.bookings.find(b => b.id === action.meta.arg.bookingId);
        if (booking) {
          Object.assign(booking, action.meta.arg.updates, {
            updatedAt: new Date().toISOString(),
          });
        }
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.isSaving = false;
        const booking = state.bookings.find(b => b.id === action.payload.bookingId);
        if (booking) {
          Object.assign(booking, action.payload.data ?? action.payload.updates, {
            updatedAt: new Date().toISOString(),
          });
        }
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to update booking';
        state.lastFetched = null;
      });

    // deleteBooking
    builder
      .addCase(deleteBooking.pending, state => {
        state.isSaving = true;
      })
      .addCase(deleteBooking.fulfilled, (state, action) => {
        state.isSaving = false;
        state.bookings = state.bookings.filter(booking => booking.id !== action.payload);
      })
      .addCase(deleteBooking.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to delete booking';
      });
  },
});

export const { clearBookings } = bookingSlice.actions;
export default bookingSlice.reducer;
